import React, { useState, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import sharepointConfig from '../config/sharepointConfig';
import { fmtDatum } from '../utils/datum';

const DAGEN_KORT = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const DAG_KEYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// Tijdlijn: 08:30 - 17:00 = 510 minuten totaal
const TL_START = 510; // 08:30 in minuten
const TL_EIND = 1020; // 17:00 in minuten
const TL_DUUR = TL_EIND - TL_START; // 510
const PAUZE_START = 750; // 12:30
const PAUZE_EIND = 780; // 13:00

// Uurstreepjes
const OCHTEND_TICKS = [
  { min: 540, label: '9' },
  { min: 600, label: '10' },
  { min: 660, label: '11' },
  { min: 720, label: '12' },
];
const MIDDAG_TICKS = [
  { min: 840, label: '14' },
  { min: 900, label: '15' },
  { min: 960, label: '16' },
];
const ALLE_TICKS = [...OCHTEND_TICKS, ...MIDDAG_TICKS];

function getMedewerkerKleur(idx) {
  return sharepointConfig.medewerkerKleuren[idx % sharepointConfig.medewerkerKleuren.length];
}

function tijdNaarMin(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function parseTijdBlok(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 2) return null;
  const start = tijdNaarMin(parts[0].trim());
  const eind = tijdNaarMin(parts[1].trim());
  if (start === null || eind === null) return null;
  return { start, eind };
}

function minNaarPct(min) {
  return ((min - TL_START) / TL_DUUR) * 100;
}

function Calendar() {
  const { verlofaanvragen, feestdagen, vakantiePerioden, roosterWijzigingen, medewerkers, flexPlanning, overuren, currentUser, isAdmin, verlofTypes } = useApp();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');

  const isEigenUser = (id) => currentUser?.id === id;

  const medewerkerKleurMap = useMemo(() => {
    const map = {};
    // Include ALL medewerkers (ook inactief/uit dienst) voor kalenderitems
    const sorted = [...medewerkers].filter(m => m.Rol !== 'flexkracht').sort((a, b) => a.Naam.localeCompare(b.Naam));
    sorted.forEach((m, i) => { map[m.Id] = m.Kleur || getMedewerkerKleur(i); });
    medewerkers.filter(m => m.Rol === 'flexkracht').forEach(m => { map[m.Id] = m.Kleur || '#E86E1C'; });
    return map;
  }, [medewerkers]);

  const activeMedewerkers = useMemo(() => {
    // Alleen actieve medewerkers tonen (geen inactief, uit dienst of verwijderd)
    return medewerkers
      .filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief')
      .sort((a, b) => a.Naam.localeCompare(b.Naam));
  },
    [medewerkers]
  );

  // Get werkblokken as continuous ranges [{start, eind}]
  const getWerkBlokken = (mw, dagKey, isWeekend) => {
    const rooster = mw.Werkrooster;
    const blokken = [];
    if (rooster && rooster[dagKey]) {
      const r = rooster[dagKey];
      const o = parseTijdBlok(r.ochtend);
      const m = parseTijdBlok(r.middag);
      const a = parseTijdBlok(r.avond);
      if (o) blokken.push(o);
      if (m) blokken.push(m);
      if (a) blokken.push(a);
    } else if (!isWeekend && !rooster) {
      blokken.push({ start: 510, eind: 750 }); // 08:30-12:30
      blokken.push({ start: 780, eind: 1020 }); // 13:00-17:00
    }
    return blokken;
  };

  // Get verlof items for medewerker on date, respecting werkrooster
  const getVerlofOpDag = (mwId, dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    const mw = medewerkers.find(m => m.Id === mwId);
    const dagVdWeek = new Date(dateStr + 'T12:00:00').getDay(); // 0=zo, 6=za
    const isWeekendDag = dagVdWeek === 0 || dagVdWeek === 6;
    const dagKey = DAG_KEYS[dagVdWeek === 0 ? 6 : dagVdWeek - 1]; // map to ma..zo
    const werkBlokken = mw ? getWerkBlokken(mw, dagKey, isWeekendDag) : [];
    const werktOpDag = werkBlokken.length > 0;

    return verlofaanvragen.filter(v => {
      if (v.medewerkerId !== mwId) return false;
      if (v.Status !== 'goedgekeurd' && v.Status !== 'ingediend') return false;
      if (dateStr < v.StartDatum) return false;
      // Niet tonen op dagen dat medewerker niet werkt volgens rooster
      if (!werktOpDag) return false;
      // Ziek: tonen op werkdagen tot einddatum (of vandaag bij open ziek)
      if (v.Type === 'ziek') {
        if (v.isOpenZiek) return dateStr <= today;
        return dateStr <= v.EindDatum;
      }
      // Open ziekmelding: geen einddatum, loopt door tot vandaag
      if (v.isOpenZiek) return dateStr <= today;
      return dateStr <= v.EindDatum;
    });
  };

  // Get overuren for medewerker on specific date (alle statussen behalve afgekeurd)
  const getOverurenOpDag = (mwId, dateStr) => {
    return overuren.filter(o =>
      (o.medewerkerId === mwId) &&
      (o.datum === dateStr) &&
      (o.status || o.Status) !== 'afgekeurd'
    );
  };

  // ===== TIMELINE BAR COMPONENT =====
  const TimelineBar = ({ mw, dateStr, dagKey, isWeekend, height = 22, showLabels = false }) => {
    const kleur = medewerkerKleurMap[mw.Id];
    const werkBlokken = getWerkBlokken(mw, dagKey, isWeekend);
    const verlofItems = getVerlofOpDag(mw.Id, dateStr);
    const ouItems = getOverurenOpDag(mw.Id, dateStr);

    // Check roosterwijzigingen: is deze datum een vervangDatum (doorgestreept)?
    const isVervangen = roosterWijzigingen.some(rw => rw.medewerkerId === mw.Id && rw.vervangDatum === dateStr);
    // Check roosterwijzigingen: werkt medewerker extra op deze datum?
    const extraWerk = roosterWijzigingen.filter(rw => rw.medewerkerId === mw.Id && rw.datum === dateStr);
    const heeftExtraWerk = extraWerk.length > 0;

    // Determine effective werkblokken: use extra werk tijden if on non-workday, or normal rooster
    let effectieveBlokken = werkBlokken;
    if (heeftExtraWerk && werkBlokken.length === 0) {
      // Niet-standaard werkdag maar werkt hier toch (roosterwijziging)
      effectieveBlokken = extraWerk.map(rw => {
        if (rw.heleDag) return [{ start: 510, eind: 750 }, { start: 780, eind: 1020 }]; // 08:30-12:30, 13:00-17:00
        const s = rw.startTijd ? parseInt(rw.startTijd.split(':')[0]) * 60 + parseInt(rw.startTijd.split(':')[1]) : 510;
        const e = rw.eindTijd ? parseInt(rw.eindTijd.split(':')[0]) * 60 + parseInt(rw.eindTijd.split(':')[1]) : 1020;
        return [{ start: s, eind: e }];
      }).flat();
    }

    const werkt = effectieveBlokken.length > 0;

    if (!werkt && verlofItems.length === 0 && ouItems.length === 0 && !isVervangen) {
      return <div style={{ height, background: '#f8f7f6', borderRadius: 2 }} />;
    }

    return (
      <div style={{ position: 'relative', height, background: '#f8f7f6', borderRadius: 2, overflow: 'hidden' }}>
        {/* Vervangen (doorgestreepte) werkblokken — origineel rooster dat vervalt */}
        {isVervangen && werkBlokken.map((b, i) => {
          const left = minNaarPct(Math.max(b.start, TL_START));
          const right = 100 - minNaarPct(Math.min(b.eind, TL_EIND));
          return (
            <div key={`v-${i}`} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${left}%`, right: `${right}%`,
              background: 'repeating-linear-gradient(135deg, transparent, transparent 3px, #D1343830 3px, #D1343830 4px)',
              borderTop: `2px dashed #D13438`,
              opacity: 0.5, zIndex: 0,
            }} />
          );
        })}
        {/* Werkblokken (normaal of extra) */}
        {!isVervangen && effectieveBlokken.map((b, i) => {
          const left = minNaarPct(Math.max(b.start, TL_START));
          const right = 100 - minNaarPct(Math.min(b.eind, TL_EIND));
          const isExtra = heeftExtraWerk && werkBlokken.length === 0;
          return (
            <div key={i} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${left}%`, right: `${right}%`,
              background: isExtra ? '#0369A115' : `${kleur}20`,
              borderTop: `2px solid ${isExtra ? '#0369A1' : kleur}`,
            }} />
          );
        })}

        {/* Pauze overlay (alleen doordeweeks als er ochtend+middag is) */}
        {!isWeekend && !isVervangen && effectieveBlokken.some(b => b.eind >= PAUZE_START) && effectieveBlokken.some(b => b.start <= PAUZE_EIND) && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${minNaarPct(PAUZE_START)}%`,
            width: `${(30 / TL_DUUR) * 100}%`,
            background: 'repeating-linear-gradient(45deg, #d8d6d4, #d8d6d4 1px, #eeedeb 1px, #eeedeb 3px)',
            zIndex: 1,
          }} />
        )}

        {/* Verlof overlays */}
        {verlofItems.map((v, vi) => {
          const typeInfo = verlofTypes.find(t => t.key === v.Type) || {};
          const vKleur = typeInfo.color || '#767676';
          const isPending = v.Status === 'ingediend';
          const isMultiDay = v.StartDatum && v.EindDatum && v.StartDatum !== v.EindDatum;

          // Determine which time segments to overlay
          let segments = [];

          if (v.StartTijd && v.EindTijd && isMultiDay) {
            const isFirstDay = dateStr === v.StartDatum;
            const isLastDay = dateStr === v.EindDatum;
            if (isFirstDay) {
              segments.push({ start: tijdNaarMin(v.StartTijd), eind: effectieveBlokken.length > 0 ? Math.max(...effectieveBlokken.map(b => b.eind)) : TL_EIND });
            } else if (isLastDay) {
              segments.push({ start: effectieveBlokken.length > 0 ? Math.min(...effectieveBlokken.map(b => b.start)) : TL_START, eind: tijdNaarMin(v.EindTijd) });
            } else {
              // Tussenliggende dag: alleen werkblokken
              segments = effectieveBlokken.length > 0 ? effectieveBlokken.map(b => ({ start: b.start, eind: b.eind })) : [{ start: TL_START, eind: TL_EIND }];
            }
          } else if (v.StartTijd && v.EindTijd) {
            segments.push({ start: tijdNaarMin(v.StartTijd), eind: tijdNaarMin(v.EindTijd) });
          } else {
            // Hele dag: overlay volgens werkblokken (niet hele tijdlijn)
            segments = effectieveBlokken.length > 0 ? effectieveBlokken.map(b => ({ start: b.start, eind: b.eind })) : [{ start: TL_START, eind: TL_EIND }];
          }

          return segments.map((seg, si) => {
            const left = minNaarPct(Math.max(seg.start, TL_START));
            const right = 100 - minNaarPct(Math.min(seg.eind, TL_EIND));
            const barWidth = 100 - left - right;
            return (
              <div key={`${vi}-${si}`} style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${left}%`, right: `${right}%`,
                background: `${vKleur}50`,
                borderTop: `3px solid ${vKleur}`,
                borderBottom: isPending ? `2px dashed ${vKleur}` : `2px solid ${vKleur}`,
                zIndex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', gap: 3,
              }} title={`${typeInfo.text || v.Type} (${v.Status})${v.StartTijd ? ` ${v.StartTijd}-${v.EindTijd}` : ''}`}>
                {si === 0 && (
                  <span style={{
                    fontSize: showLabels ? 11 : 9, fontWeight: 800, color: '#fff',
                    background: vKleur, borderRadius: 3,
                    padding: showLabels ? '1px 5px' : '0px 3px',
                    lineHeight: 1.3, letterSpacing: 0.3,
                    whiteSpace: 'nowrap',
                  }}>
                    {showLabels && barWidth > 25 ? (typeInfo.text || v.Type) : (typeInfo.label || 'V')}
                  </span>
                )}
                {si === 0 && showLabels && (
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    color: isPending ? '#FFB900' : '#fff',
                    background: isPending ? '#fff' : '#107C10',
                    borderRadius: 3, padding: '0px 3px', lineHeight: 1.4,
                  }}>{isPending ? '⏳' : '✓'}</span>
                )}
              </div>
            );
          });
        })}

        {/* Overuren overlays — styled like flex planning bars */}
        {ouItems.map((o, oi) => {
          const oStart = tijdNaarMin(o.startTijd) || null;
          const oEind = tijdNaarMin(o.eindTijd) || null;
          if (oStart === null || oEind === null) return null;
          const left = minNaarPct(Math.max(oStart, TL_START));
          const right = 100 - minNaarPct(Math.min(oEind, TL_EIND));
          const isPending = (o.status || o.Status) === 'ingediend';
          const barWidth = 100 - left - right;
          return (
            <div key={`ou-${oi}`} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${Math.max(0, left)}%`, right: `${Math.max(0, right)}%`,
              background: '#FF980030', borderTop: `2px solid #FF9800`,
              borderBottom: isPending ? '2px dashed #FF9800' : 'none',
              zIndex: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', gap: 3,
            }} title={`⏰ ${o.uren || o.Uren || 0}u overuren ${o.startTijd}–${o.eindTijd} (${o.status || o.Status})`}>
              <span style={{
                fontSize: showLabels ? 11 : 8, fontWeight: 800, color: '#fff',
                background: '#FF9800', borderRadius: 3,
                padding: showLabels ? '1px 5px' : '0px 3px',
                lineHeight: 1.3, whiteSpace: 'nowrap',
              }}>
                {showLabels && barWidth > 20 ? `⏰ ${o.uren || o.Uren || 0}u` : '⏰'}
              </span>
              {showLabels && (
                <span style={{
                  fontSize: 9, fontWeight: 700,
                  color: isPending ? '#FFB900' : '#fff',
                  background: isPending ? '#fff' : '#107C10',
                  borderRadius: 3, padding: '0px 3px', lineHeight: 1.4,
                }}>{isPending ? '⏳' : '✓'}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ===== TIMELINE RULER (tick marks) =====
  const TimelineRuler = ({ height = 18, compact = false }) => (
    <div style={{ position: 'relative', height, background: '#faf9f8', borderBottom: '1px solid #edebe9' }}>
      {/* Pauze zone */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0,
        left: `${minNaarPct(PAUZE_START)}%`,
        width: `${(30 / TL_DUUR) * 100}%`,
        background: '#f0efed',
      }} />
      {/* Tick marks */}
      {ALLE_TICKS.map(tick => {
        const left = minNaarPct(tick.min);
        return (
          <div key={tick.min} style={{
            position: 'absolute', left: `${left}%`, top: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            transform: 'translateX(-50%)',
          }}>
            <div style={{ width: 1, height: compact ? 4 : 6, background: '#c8c6c4' }} />
            <span style={{ fontSize: compact ? 6 : 8, color: '#a19f9d', fontWeight: 600, lineHeight: 1 }}>
              {tick.label}
            </span>
          </div>
        );
      })}
      {/* Pauze label */}
      {!compact && (
        <div style={{
          position: 'absolute',
          left: `${minNaarPct(PAUZE_START)}%`,
          width: `${(30 / TL_DUUR) * 100}%`,
          bottom: 0, top: 0,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 6, color: '#c8c6c4', fontWeight: 600 }}>P</span>
        </div>
      )}
    </div>
  );

  // ===== DATA =====
  const weekDays = useMemo(() => {
    const d = new Date(currentDate);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - day + 1);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dateStr = d.toISOString().split('T')[0];
      const date = new Date(dateStr + 'T12:00:00');
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const feestdag = feestdagen.find(f => f.Datum === dateStr);
      const vakantiePeriode = vakantiePerioden?.find(vp => dateStr >= vp.startDatum && dateStr <= vp.eindDatum);
      days.push({ dateStr, date, dayOfWeek, isWeekend, isToday, feestdag, vakantiePeriode, dagKey: DAG_KEYS[i] });
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [currentDate, feestdagen, vakantiePerioden]);

  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay() || 7;
    const days = [];
    for (let i = 1; i < startDay; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const date = new Date(dateStr + 'T12:00:00');
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const feestdag = feestdagen.find(f => f.Datum === dateStr);
      const vakantiePeriode = vakantiePerioden?.find(vp => dateStr >= vp.startDatum && dateStr <= vp.eindDatum);
      const dagKey = DAG_KEYS[(dayOfWeek + 6) % 7];
      days.push({ dateStr, date, isWeekend, isToday, feestdag, vakantiePeriode, dagKey, dayNum: d });
    }
    return days;
  }, [currentDate, feestdagen, vakantiePerioden]);

  // Navigation
  const navigate = (delta) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setCurrentDate(d);
  };
  const goToToday = () => setCurrentDate(new Date());

  const headerLabel = () => {
    if (view === 'month') return currentDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const mon = weekDays[0]?.date;
      const sun = weekDays[6]?.date;
      if (!mon || !sun) return '';
      return `${mon.getDate()} ${mon.toLocaleDateString('nl-NL', { month: 'short' })} — ${sun.getDate()} ${sun.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // ===== WEEK VIEW =====
  const WeekView = () => {
    const flexPerDag = {};
    weekDays.forEach(d => { flexPerDag[d.dateStr] = flexPlanning.filter(p => p.datum === d.dateStr); });
    const hasAnyFlex = Object.values(flexPerDag).some(a => a.length > 0);

    // Check per weekenddag of er mutaties zijn + bereken tijdbereik
    const weekendInfo = {};
    weekDays.filter(d => d.isWeekend).forEach(d => {
      let minTijd = Infinity, maxTijd = -Infinity;
      let heeftMutatie = false;

      activeMedewerkers.forEach(m => {
        // Verlof
        getVerlofOpDag(m.Id, d.dateStr).forEach(v => {
          heeftMutatie = true;
          const s = tijdNaarMin(v.StartTijd);
          const e = tijdNaarMin(v.EindTijd);
          if (s !== null) minTijd = Math.min(minTijd, s);
          if (e !== null) maxTijd = Math.max(maxTijd, e);
        });
        // Overuren
        getOverurenOpDag(m.Id, d.dateStr).forEach(o => {
          heeftMutatie = true;
          const s = tijdNaarMin(o.startTijd);
          const e = tijdNaarMin(o.eindTijd);
          if (s !== null) minTijd = Math.min(minTijd, s);
          if (e !== null) maxTijd = Math.max(maxTijd, e);
        });
      });
      // Flex
      (flexPerDag[d.dateStr] || []).forEach(p => {
        heeftMutatie = true;
        const s = tijdNaarMin(p.startTijd);
        const e = tijdNaarMin(p.eindTijd);
        if (s !== null) minTijd = Math.min(minTijd, s);
        if (e !== null) maxTijd = Math.max(maxTijd, e);
      });
      // Roosterwijzigingen
      roosterWijzigingen.filter(rw => rw.datum === d.dateStr).forEach(rw => {
        heeftMutatie = true;
        if (!rw.heleDag) {
          const s = tijdNaarMin(rw.startTijd);
          const e = tijdNaarMin(rw.eindTijd);
          if (s !== null) minTijd = Math.min(minTijd, s);
          if (e !== null) maxTijd = Math.max(maxTijd, e);
        }
      });

      if (heeftMutatie && minTijd !== Infinity) {
        // +/- 1 uur marge, begrensd op timeline grenzen
        minTijd = Math.max(TL_START, minTijd - 60);
        maxTijd = Math.min(TL_EIND, maxTijd + 60);
        const range = maxTijd - minTijd;
        weekendInfo[d.dateStr] = { heeftMutatie: true, ratio: Math.max(0.2, range / TL_DUUR) };
      } else if (heeftMutatie) {
        // Mutatie zonder tijden (hele dag) — geef 40% breedte
        weekendInfo[d.dateStr] = { heeftMutatie: true, ratio: 0.4 };
      } else {
        weekendInfo[d.dateStr] = { heeftMutatie: false, ratio: 0 };
      }
    });

    // Bereken kolombreedtes: werkdagen gewicht=1, weekenden proportioneel
    const dagGewichten = weekDays.map(d => {
      if (!d.isWeekend) return 1;
      const info = weekendInfo[d.dateStr];
      if (!info?.heeftMutatie) return 0; // wordt 40px fixed
      return info.ratio;
    });
    const totaalGewicht = dagGewichten.reduce((s, w) => s + w, 0);

    return (
      <div style={st.weekContainer}>
        <div>
          <table style={{ ...st.roosterTable, minWidth: 700 }}>
            <colgroup>
              <col style={{ width: 28 }} />   {/* sticky avatar kolom */}
              <col style={{ width: 72 }} />   {/* naam (scrolt mee) */}
              {weekDays.map((d, i) => {
                if (d.isWeekend && !weekendInfo[d.dateStr]?.heeftMutatie) {
                  return <col key={i} style={{ width: 40 }} />;
                }
                // Proportionele breedte: werkdag=1, weekend=ratio
                const pct = totaalGewicht > 0 ? (dagGewichten[i] / totaalGewicht) * 100 : 14;
                return <col key={i} style={{ width: `${pct}%` }} />;
              })}
            </colgroup>
            <thead>
              <tr>
                {/* Sticky lege hoek voor avatar-kolom */}
                <th style={{ ...st.th, position: 'sticky', left: 0, zIndex: 3, background: '#fff', borderRight: 'none', padding: 0, width: 28 }} />
                {/* Naam-header (scrolt mee) */}
                <th style={{ ...st.th, background: '#fff', borderRight: '2px solid #edebe9', textAlign: 'left', paddingLeft: 4 }} />
                {weekDays.map((day, i) => (
                  <th key={i} style={{
                    ...st.th, padding: '4px 0',
                    background: day.feestdag ? '#fde7e9' : day.vakantiePeriode ? '#FFF8E1' : day.isToday ? '#E86E1C' : day.isWeekend ? '#f5f4f3' : '#fff',
                    color: day.isToday ? '#fff' : day.feestdag ? '#D83B01' : day.vakantiePeriode ? '#7A6400' : '#323130',
                    cursor: 'pointer',
                  }} onClick={() => { setCurrentDate(new Date(day.dateStr + 'T12:00:00')); setView('day'); }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', lineHeight: 1 }}>{DAGEN_KORT[i]}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}>{day.date.getDate()}</div>
                    {day.feestdag && <div style={{ fontSize: 7, fontWeight: 600, lineHeight: 1, marginTop: 1 }}>{day.feestdag.Naam}</div>}
                    {day.vakantiePeriode && !day.feestdag && <div style={{ fontSize: 7, fontWeight: 600, lineHeight: 1, marginTop: 1 }}>🏖️ {day.vakantiePeriode.naam}</div>}
                  </th>
                ))}
              </tr>
              {/* Timeline ruler row */}
              <tr>
                <td style={{ position: 'sticky', left: 0, zIndex: 2, background: '#faf9f8', borderRight: 'none', borderBottom: '1px solid #edebe9', padding: 0, width: 28 }} />
                <td style={{ background: '#faf9f8', borderRight: '2px solid #edebe9', borderBottom: '1px solid #edebe9', padding: 0 }}>
                  <div style={{ height: 18, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', paddingRight: 4 }}>
                    <span style={{ fontSize: 7, color: '#c8c6c4' }}>08:30</span>
                  </div>
                </td>
                {weekDays.map((day, i) => (
                  <td key={i} style={{ padding: 0, borderBottom: '1px solid #edebe9', borderRight: '1px solid #edebe9' }}>
                    {(!day.isWeekend || weekendInfo[day.dateStr]?.heeftMutatie) ? <TimelineRuler height={18} compact /> : (
                      <div style={{ height: 18, background: '#f5f4f3', borderBottom: '1px solid #edebe9' }} />
                    )}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeMedewerkers.map((mw, mIdx) => {
                const kleur = medewerkerKleurMap[mw.Id];
                const even = mIdx % 2 === 0;
                const bg = even ? '#fff' : '#faf9f8';
                return (
                  <tr key={mw.Id}>
                    {/* Sticky avatar-cirkel — blok 1 */}
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 2,
                      background: bg,
                      borderRight: 'none', padding: '2px 3px',
                      verticalAlign: 'middle', textAlign: 'center',
                      borderBottom: '1px solid #f3f2f1',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', background: kleur,
                        color: '#fff', fontSize: 9, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: mw.Status === 'inactief' || mw.Status === 'uitdienst' ? 0.5 : 1,
                        margin: '0 auto',
                      }}>{mw.Naam?.charAt(0)}</div>
                    </td>
                    {/* Naam-kolom — scrolt mee */}
                    <td style={{
                      ...st.naamCel, background: bg,
                      borderRight: '2px solid #edebe9', padding: '2px 4px',
                      maxWidth: 72, overflow: 'hidden',
                    }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: mw.Status === 'inactief' || mw.Status === 'uitdienst' ? '#a19f9d' : '#323130',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                        fontStyle: mw.Status === 'uitdienst' ? 'italic' : 'normal',
                      }}>{mw.Naam?.split(' ')[0]}{mw.Status === 'uitdienst' ? ' ⊘' : mw.Status === 'inactief' ? ' ⏸' : ''}</span>
                    </td>
                    {weekDays.map((day, dIdx) => {
                      const verlofOpDag = getVerlofOpDag(mw.Id, day.dateStr);
                      const werkBlokken = getWerkBlokken(mw, day.dagKey, day.isWeekend);
                      const heeftZiek = verlofOpDag.some(v => v.Type === 'ziek');
                      const heeftPending = verlofOpDag.some(v => v.Status === 'ingediend');
                      const heeftGoedgekeurd = verlofOpDag.some(v => v.Status === 'goedgekeurd' && v.Type !== 'ziek');
                      const werkt = werkBlokken.length > 0;
                      let dotColor = null;
                      if (heeftZiek) dotColor = '#E86E1C';
                      else if (heeftGoedgekeurd) dotColor = '#D13438';
                      else if (heeftPending) dotColor = '#E86E1C';
                      else if (werkt && !day.isWeekend) dotColor = '#107C10';
                      return (
                        <td key={dIdx} style={{
                          padding: '2px 1px', borderRight: '1px solid #f3f2f1', borderBottom: '1px solid #f3f2f1',
                          background: even ? (day.isWeekend ? '#faf9f8' : '#fff') : (day.isWeekend ? '#f5f4f3' : '#faf9f8'),
                          position: 'relative',
                        }}>
                          {dotColor && <div style={{ position: 'absolute', top: 1, right: 1, width: 6, height: 6, borderRadius: '50%', background: dotColor, zIndex: 3, boxShadow: '0 0 0 1px #fff' }} />}
                          <TimelineBar mw={mw} dateStr={day.dateStr} dagKey={day.dagKey} isWeekend={day.isWeekend} height={20} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Flex workers */}
              {(() => {
                const allFlexEntries = [];
                weekDays.forEach(day => {
                  (flexPerDag[day.dateStr] || []).forEach(p => {
                    if (!allFlexEntries.find(e => e.flexkrachtId === p.flexkrachtId)) {
                      allFlexEntries.push(p);
                    }
                  });
                });
                return allFlexEntries.map((flexMw, fIdx) => (
                  <tr key={flexMw.flexkrachtId} style={{ borderTop: fIdx === 0 ? '2px solid #E86E1C40' : 'none' }}>
                    {/* Sticky avatar ⚡ — blok 1 */}
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 2, background: '#FFF4EE',
                      borderRight: 'none', padding: '2px 3px',
                      verticalAlign: 'middle', textAlign: 'center',
                      borderBottom: '1px solid #f3f2f1',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', background: '#E86E1C',
                        color: '#fff', fontSize: 10, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto',
                      }}>⚡</div>
                    </td>
                    {/* Naam (scrolt mee) */}
                    <td style={{ ...st.naamCel, background: '#FFF4EE', borderRight: '2px solid #edebe9', padding: '2px 4px', maxWidth: 72, overflow: 'hidden' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#E86E1C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {flexMw.flexkrachtNaam?.split(' ')[0]}
                      </span>
                    </td>
                    {weekDays.map((day, dIdx) => {
                      const dagItems = (flexPerDag[day.dateStr] || []).filter(p => p.flexkrachtId === flexMw.flexkrachtId);
                      return (
                        <td key={dIdx} style={{
                          padding: '2px 1px', borderRight: '1px solid #f3f2f1', borderBottom: '1px solid #f3f2f1',
                          background: day.isWeekend ? '#FFF8F2' : '#FFF4EE',
                        }}>
                          {dagItems.length > 0 ? (
                            <div style={{ position: 'relative', height: 20, background: '#f8f7f6', borderRadius: 2, overflow: 'hidden' }}>
                              {dagItems.map(p => {
                                const fStart = tijdNaarMin(p.startTijd) || TL_START;
                                const fEind = tijdNaarMin(p.eindTijd) || TL_EIND;
                                const left = minNaarPct(Math.max(fStart, TL_START));
                                const right = 100 - minNaarPct(Math.min(fEind, TL_EIND));
                                return (
                                  <div key={p.id} style={{
                                    position: 'absolute', top: 0, bottom: 0,
                                    left: `${Math.max(0, left)}%`, right: `${Math.max(0, right)}%`,
                                    background: '#E86E1C30', borderTop: '2px solid #E86E1C',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    overflow: 'hidden',
                                  }} title={`${p.flexkrachtNaam} ${p.startTijd}–${p.eindTijd} (${p.uren}u)`}>
                                    <span style={{ fontSize: 7, fontWeight: 700, color: '#E86E1C', whiteSpace: 'nowrap' }}>
                                      {p.startTijd}–{p.eindTijd}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ height: 20, background: 'transparent' }} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ===== DAY VIEW =====
  const DayView = () => {
    const dateStr = currentDate.toISOString().split('T')[0];
    const date = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dagKey = DAG_KEYS[(dayOfWeek + 6) % 7];
    const feestdag = feestdagen.find(f => f.Datum === dateStr);
    const vakantiePeriode = vakantiePerioden?.find(vp => dateStr >= vp.startDatum && dateStr <= vp.eindDatum);
    const flexItems = flexPlanning.filter(p => p.datum === dateStr);

    return (
      <div style={st.weekContainer}>
        {feestdag && (
          <div style={{ background: '#fde7e9', color: '#D83B01', padding: '8px 16px', borderRadius: 8, fontWeight: 600, marginBottom: 12, textAlign: 'center' }}>
            🎉 {feestdag.Naam}
          </div>
        )}
        {vakantiePeriode && !feestdag && (
          <div style={{ background: '#FFF4CE', color: '#7A6400', padding: '8px 16px', borderRadius: 8, fontWeight: 600, marginBottom: 12, textAlign: 'center' }}>
            🏖️ {vakantiePeriode.naam}
          </div>
        )}
        <div>
          <table style={{ ...st.roosterTable, minWidth: 560 }}>
            <colgroup>
              <col style={{ width: 36 }} />    {/* sticky avatar */}
              <col style={{ width: 72 }} />    {/* naam — scrolt */}
              <col />                           {/* timeline */}
              <col style={{ width: 220 }} />   {/* details */}
            </colgroup>
            <thead>
              <tr>
                {/* Sticky lege hoek */}
                <th style={{ ...st.th, position: 'sticky', left: 0, zIndex: 3, background: '#fff', borderRight: 'none', padding: 0, width: 36 }} />
                {/* Naam-header scrolt mee */}
                <th style={{ ...st.th, background: '#fff', borderRight: '2px solid #edebe9', textAlign: 'left', paddingLeft: 6 }}>Medewerker</th>
                <th style={{ ...st.th, padding: 0 }}>
                  <div style={{ padding: '4px 8px', fontSize: 11, color: '#605e5c' }}>08:30 — 17:00</div>
                  {!isWeekend && <TimelineRuler height={22} />}
                </th>
                <th style={{ ...st.th }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {activeMedewerkers.map((mw, mIdx) => {
                const kleur = medewerkerKleurMap[mw.Id];
                const even = mIdx % 2 === 0;
                const bg = even ? '#fff' : '#faf9f8';
                const verlofItems = getVerlofOpDag(mw.Id, dateStr);
                const werkBlokken = getWerkBlokken(mw, dagKey, isWeekend);
                const werkt = werkBlokken.length > 0;

                return (
                  <tr key={mw.Id} style={{ background: bg }}>
                    {/* Sticky avatar-cirkel — blok 1 */}
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 3, background: bg,
                      borderRight: 'none', padding: '4px 3px',
                      verticalAlign: 'middle', textAlign: 'center',
                      borderBottom: '1px solid #f3f2f1',
                    }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', background: kleur,
                          color: '#fff', fontSize: 12, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: mw.Status === 'inactief' || mw.Status === 'uitdienst' ? 0.5 : 1,
                        }}>{mw.Naam?.charAt(0)}</div>
                        {(() => {
                          const heeftZiek = verlofItems.some(v => v.Type === 'ziek');
                          const heeftPending = verlofItems.some(v => v.Status === 'ingediend');
                          const heeftGoedgekeurd = verlofItems.some(v => v.Status === 'goedgekeurd' && v.Type !== 'ziek');
                          const heeftOveruren = getOverurenOpDag(mw.Id, dateStr).length > 0;
                          const dotClr = heeftZiek ? '#E86E1C' : heeftGoedgekeurd ? '#D13438' : heeftPending ? '#E86E1C' : (werkt && !isWeekend) ? '#107C10' : heeftOveruren ? '#E65100' : null;
                          return dotClr ? <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: dotClr, border: '2px solid #fff' }} /> : null;
                        })()}
                      </div>
                    </td>
                    {/* Naam-kolom — scrolt mee */}
                    <td style={{ ...st.naamCel, background: bg, borderRight: '2px solid #edebe9', padding: '4px 6px', borderBottom: '1px solid #f3f2f1' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: mw.Status === 'inactief' || mw.Status === 'uitdienst' ? '#a19f9d' : '#323130', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {mw.Naam?.split(' ')[0]}{mw.Status === 'uitdienst' ? ' ⊘' : mw.Status === 'inactief' ? ' ⏸' : ''}
                      </div>
                    </td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #f3f2f1', borderBottom: '1px solid #f3f2f1' }}>
                      <TimelineBar mw={mw} dateStr={dateStr} dagKey={dagKey} isWeekend={isWeekend} height={32} showLabels />
                    </td>
                    <td style={{ padding: '6px 12px', fontSize: 12, color: '#605e5c', verticalAlign: 'middle', borderBottom: '1px solid #f3f2f1' }}>
                      {(() => {
                        const rwItems = roosterWijzigingen.filter(rw => rw.medewerkerId === mw.Id && rw.datum === dateStr);
                        const isVervangen = roosterWijzigingen.some(rw => rw.medewerkerId === mw.Id && rw.vervangDatum === dateStr);
                        const vervangenDoor = isVervangen ? roosterWijzigingen.find(rw => rw.medewerkerId === mw.Id && rw.vervangDatum === dateStr) : null;
                        const werkt2 = getWerkBlokken(mw, dagKey, isWeekend).length > 0 || rwItems.length > 0;
                        const ouItems = getOverurenOpDag(mw.Id, dateStr);
                        return (
                          <>
                            {isVervangen && (
                              <div style={{ marginBottom: 3 }}>
                                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: '#fde7e9', color: '#D13438', fontWeight: 600, fontSize: 11, textDecoration: 'line-through' }}>
                                  Vervalt
                                </span>
                                {vervangenDoor && <span style={{ marginLeft: 6, fontSize: 10, color: '#605e5c' }}>→ werkt {fmtDatum(vervangenDoor.datum)}</span>}
                              </div>
                            )}
                            {rwItems.length > 0 && rwItems.map((rw, ri) => (
                              <div key={`rw-${ri}`} style={{ marginBottom: 3 }}>
                                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: '#E0F2FE', color: '#0369A1', fontWeight: 600, fontSize: 11 }}>
                                  🔄 {rw.heleDag ? 'Hele dag' : `${rw.startTijd}–${rw.eindTijd}`}
                                </span>
                                {rw.omschrijving && <span style={{ marginLeft: 6, fontSize: 10, color: '#605e5c' }}>{rw.omschrijving}</span>}
                                {rw.vervangDatum && <span style={{ marginLeft: 6, fontSize: 10, color: '#D13438' }}>i.p.v. {fmtDatum(rw.vervangDatum)}</span>}
                              </div>
                            ))}
                            {!werkt2 && rwItems.length === 0 ? null : verlofItems.length > 0 ? (
                              verlofItems.map((v, vi) => {
                                const ti = verlofTypes.find(t => t.key === v.Type) || {};
                                const isPending = v.Status === 'ingediend';
                                return (
                                  <div key={vi} style={{ marginBottom: 3 }}>
                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: `${ti.color || '#767676'}20`, color: ti.color, fontWeight: 600, fontSize: 11 }}>{ti.text || v.Type}</span>
                                    {v.StartTijd && v.EindTijd && (() => {
                                      const isMultiDay = v.StartDatum && v.EindDatum && v.StartDatum !== v.EindDatum;
                                      if (isMultiDay) {
                                        if (dateStr === v.StartDatum) return <span style={{ marginLeft: 6, fontSize: 11, color: '#605e5c' }}>vanaf {v.StartTijd}</span>;
                                        if (dateStr === v.EindDatum) return <span style={{ marginLeft: 6, fontSize: 11, color: '#605e5c' }}>tot {v.EindTijd}</span>;
                                        return <span style={{ marginLeft: 6, fontSize: 11, color: '#605e5c' }}>hele dag</span>;
                                      }
                                      return <span style={{ marginLeft: 6, fontSize: 11, color: '#605e5c' }}>{v.StartTijd}–{v.EindTijd}</span>;
                                    })()}
                                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: isPending ? '#FFB900' : '#107C10' }}>{isPending ? '⏳ Ingediend' : '✓ Goedgekeurd'}</span>
                                    {(isAdmin || isEigenUser(mw.Id)) && v.Reden && (
                                      <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 1 }}>"{v.Reden}"</div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <span style={{ color: '#107C10', fontSize: 11 }}>
                                {werkBlokken.map((b, i) => {
                                  const s = `${String(Math.floor(b.start/60)).padStart(2,'0')}:${String(b.start%60).padStart(2,'0')}`;
                                  const e = `${String(Math.floor(b.eind/60)).padStart(2,'0')}:${String(b.eind%60).padStart(2,'0')}`;
                                  return <span key={i}>{i > 0 ? ' · ' : ''}{s}–{e}</span>;
                                })}
                              </span>
                            )}
                            {ouItems.length > 0 && ouItems.map((o, oi) => {
                              const st2 = o.status || o.Status;
                              const isPending = st2 === 'ingediend';
                              const isUitbetaald = st2 === 'uitbetaald';
                              return (
                                <div key={`ou-${oi}`} style={{ marginBottom: 3, marginTop: oi === 0 ? 4 : 0 }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: '#FFF3E0', color: '#E65100', fontWeight: 600, fontSize: 11 }}>
                                    ⏰ {o.uren || o.Uren || 0}u overuren
                                  </span>
                                  {o.startTijd && o.eindTijd && <span style={{ marginLeft: 6, fontSize: 11, color: '#605e5c' }}>{o.startTijd}–{o.eindTijd}</span>}
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: isPending ? '#FFB900' : isUitbetaald ? '#8764B8' : '#107C10' }}>
                                    {isPending ? '⏳ Ingediend' : isUitbetaald ? '💰 Uitbetaald' : '✓ Goedgekeurd'}
                                  </span>
                                  {(isAdmin || isEigenUser(mw.Id)) && (o.omschrijving || o.Omschrijving) && (
                                    <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 1 }}>"{o.omschrijving || o.Omschrijving}"</div>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
              {/* Flex rijen */}
              {flexItems.map(p => (
                <tr key={p.id} style={{ background: '#FFF4EE' }}>
                  {/* Sticky avatar ⚡ — blok 1 */}
                  <td style={{
                    position: 'sticky', left: 0, zIndex: 3, background: '#FFF4EE',
                    borderRight: 'none', padding: '4px 3px',
                    verticalAlign: 'middle', textAlign: 'center',
                    borderBottom: '1px solid #f3f2f1',
                  }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E86E1C', color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>⚡</div>
                  </td>
                  {/* Naam scrolt mee */}
                  <td style={{ ...st.naamCel, background: '#FFF4EE', borderRight: '2px solid #edebe9', padding: '4px 6px', borderBottom: '1px solid #f3f2f1' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#E86E1C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.flexkrachtNaam?.split(' ')[0]}</div>
                  </td>
                  <td style={{ padding: '6px 4px', borderRight: '1px solid #f3f2f1', borderBottom: '1px solid #f3f2f1' }}>
                    <div style={{ position: 'relative', height: 32, background: '#f8f7f6', borderRadius: 2, overflow: 'hidden' }}>
                      {(() => {
                        const fStart = tijdNaarMin(p.startTijd) || TL_START;
                        const fEind = tijdNaarMin(p.eindTijd) || TL_EIND;
                        const left = minNaarPct(Math.max(fStart, TL_START));
                        const right = 100 - minNaarPct(Math.min(fEind, TL_EIND));
                        return (
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${left}%`, right: `${right}%`, background: '#E86E1C30', borderTop: '2px solid #E86E1C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#E86E1C' }}>{p.startTijd}–{p.eindTijd}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td style={{ padding: '6px 12px', fontSize: 12, color: '#E86E1C', borderBottom: '1px solid #f3f2f1' }}>Flexkracht ({p.uren}u)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ===== MONTH VIEW (mini timelines) =====
  const MonthView = () => (
    <div style={st.calendarCard}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #edebe9' }}>
        {DAGEN_KORT.map(d => <div key={d} style={st.dayHeader}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {monthDays.map((item, idx) => (
          <div key={idx} style={{
            ...st.dayCell,
            background: !item ? '#f8f7f6' : item.feestdag ? '#fde7e9' : item.vakantiePeriode ? '#FFF8E1' : item.isToday ? '#fff8f0' : item.isWeekend ? '#faf9f8' : '#fff',
            boxShadow: item?.isToday ? 'inset 0 0 0 2px #E86E1C' : 'none',
            cursor: item ? 'pointer' : 'default',
          }} onClick={() => { if (item) { setCurrentDate(new Date(item.dateStr + 'T12:00:00')); setView('day'); } }}>
            {item && (
              <>
                <div style={st.dayNumber}>{item.dayNum}</div>
                {item.feestdag && <div style={{ fontSize: 7, color: '#D83B01', fontWeight: 600, lineHeight: 1, marginBottom: 1 }}>🎉</div>}
                {item.vakantiePeriode && !item.feestdag && <div style={{ fontSize: 7, color: '#7A6400', fontWeight: 600, lineHeight: 1, marginBottom: 1 }}>🏖️</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 1 }}>
                  {activeMedewerkers.map(m => {
                    const werkBlokken = getWerkBlokken(m, item.dagKey, item.isWeekend);
                    const verlofItems = getVerlofOpDag(m.Id, item.dateStr);
                    const ouItems = getOverurenOpDag(m.Id, item.dateStr);
                    const rwItems = roosterWijzigingen.filter(rw => rw.medewerkerId === m.Id && rw.datum === item.dateStr);
                    const isVervangen = roosterWijzigingen.some(rw => rw.medewerkerId === m.Id && rw.vervangDatum === item.dateStr);
                    if (werkBlokken.length === 0 && verlofItems.length === 0 && ouItems.length === 0 && rwItems.length === 0 && !isVervangen) return null;
                    const heeftZiek = verlofItems.some(v => v.Type === 'ziek');
                    const heeftPending = verlofItems.some(v => v.Status === 'ingediend');
                    const heeftGoedgekeurd = verlofItems.some(v => v.Status === 'goedgekeurd' && v.Type !== 'ziek');
                    const heeftOveruren = ouItems.length > 0;
                    const dotClr = heeftZiek ? '#E86E1C' : heeftGoedgekeurd ? '#D13438' : heeftPending ? '#E86E1C' : (werkBlokken.length > 0 && !item.isWeekend) ? '#107C10' : null;
                    return (
                      <div key={m.Id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {dotClr && <div style={{ width: 4, height: 4, borderRadius: '50%', background: dotClr, flexShrink: 0 }} />}
                        {!dotClr && heeftOveruren && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#E65100', flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <TimelineBar mw={m} dateStr={item.dateStr} dagKey={item.dagKey} isWeekend={item.isWeekend} height={5} />
                        </div>
                        {heeftOveruren && <div style={{ position: 'absolute', top: -1, left: 0, fontSize: 5, lineHeight: 1 }}>⏰</div>}
                        {rwItems.length > 0 && <div style={{ position: 'absolute', top: -1, right: 0, fontSize: 5, lineHeight: 1 }}>🔄</div>}
                        {isVervangen && <div style={{ position: 'absolute', top: -1, right: 0, fontSize: 5, lineHeight: 1 }}>✕</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Legend
  const Legend = () => (
    <div style={st.legend}>
      <div style={st.legGrp}>
        {activeMedewerkers.map(m => (
          <div key={m.Id} style={st.legItem}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: medewerkerKleurMap[m.Id], flexShrink: 0 }} />
            <span style={st.legLbl}>{m.Naam?.split(' ')[0]}</span>
          </div>
        ))}
      </div>
      <div style={st.legGrp}>
        {verlofTypes.map(t => (
          <div key={t.key} style={st.legItem}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: t.color, borderRadius: 3, padding: '1px 5px', lineHeight: 1.3 }}>{t.label}</span>
            <span style={st.legLbl}>{t.text}</span>
          </div>
        ))}
      </div>
      <div style={st.legGrp}>
        <div style={st.legItem}><span style={{ fontSize: 9, fontWeight: 700, background: '#fff', border: '1px solid #FFB900', color: '#FFB900', borderRadius: 3, padding: '0px 4px' }}>⏳</span><span style={st.legLbl}>Ingediend</span></div>
        <div style={st.legItem}><span style={{ fontSize: 9, fontWeight: 700, background: '#107C10', color: '#fff', borderRadius: 3, padding: '0px 4px' }}>✓</span><span style={st.legLbl}>Goedgekeurd</span></div>
        <div style={st.legItem}><span style={{ fontSize: 9, fontWeight: 700, background: '#FFF3E0', border: '1px solid #E65100', color: '#E65100', borderRadius: 3, padding: '0px 4px' }}>⏰</span><span style={st.legLbl}>Overuren</span></div>
        <div style={st.legItem}><div style={{ width: 14, height: 8, background: 'repeating-linear-gradient(45deg, #d8d6d4, #d8d6d4 1px, #eeedeb 1px, #eeedeb 3px)', borderRadius: 1, flexShrink: 0 }} /><span style={st.legLbl}>Pauze</span></div>
        <div style={st.legItem}><span style={{ fontSize: 9, background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 3, padding: '0px 4px' }}>🏖️</span><span style={st.legLbl}>Vakantieperiode</span></div>
        <div style={st.legItem}><span style={{ fontSize: 9, background: '#E0F2FE', border: '1px solid #0369A1', color: '#0369A1', borderRadius: 3, padding: '0px 4px' }}>🔄</span><span style={st.legLbl}>Roosterwijziging</span></div>
        <div style={st.legItem}><span style={{ fontSize: 9, background: '#fde7e9', border: '1px dashed #D13438', color: '#D13438', borderRadius: 3, padding: '0px 4px', textDecoration: 'line-through' }}>dag</span><span style={st.legLbl}>Vervalt</span></div>
      </div>
      <div style={st.legGrp}>
        <div style={st.legItem}><div style={{ width: 8, height: 8, borderRadius: '50%', background: '#107C10', flexShrink: 0 }} /><span style={st.legLbl}>Aanwezig</span></div>
        <div style={st.legItem}><div style={{ width: 8, height: 8, borderRadius: '50%', background: '#E86E1C', flexShrink: 0 }} /><span style={st.legLbl}>Ziek / Ingediend</span></div>
        <div style={st.legItem}><div style={{ width: 8, height: 8, borderRadius: '50%', background: '#D13438', flexShrink: 0 }} /><span style={st.legLbl}>Afwezig</span></div>
      </div>
    </div>
  );

  return (
    <div style={st.container} className="r-cal-container">
      {/* Sticky zone: header nav + view buttons + legend */}
      <div style={st.stickyHeader} className="r-cal-sticky-hdr">
        <div style={{ ...st.header, maxWidth: '100%', overflow: 'hidden' }} className="r-cal-header">
          <div style={{ ...st.navRow, flexShrink: 0 }}>
            <button style={st.navBtn} onClick={() => navigate(-1)}>◀</button>
            <h2 style={{ ...st.monthTitle, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{headerLabel()}</h2>
            <button style={st.navBtn} onClick={() => navigate(1)}>▶</button>
          </div>
          <div style={{ ...st.btnRow, flexShrink: 0 }} className="r-cal-btns">
            {[{ key: 'month', label: 'Maand' }, { key: 'week', label: 'Week' }, { key: 'day', label: 'Dag' }].map(v => (
              <button key={v.key} style={{ ...st.viewBtn, ...(view === v.key ? st.viewBtnActive : {}), padding: '6px 12px', fontSize: 12 }} onClick={() => setView(v.key)}>{v.label}</button>
            ))}
            <button style={{ ...st.todayBtn, padding: '6px 12px', fontSize: 12 }} onClick={goToToday}>Vandaag</button>
          </div>
        </div>
        {/* Legend */}
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Legend />
        </div>
      </div>
      {/* Maandweergave scrolt horizontaal via outer wrapper */}
      {view === 'month' && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }} className="r-cal-scroll">
          <MonthView />
        </div>
      )}
      {/* Week/dag — één scroll-container, alles scrollt als geheel */}
      {view === 'week' && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }} className="r-cal-week-scroll">
          <WeekView />
        </div>
      )}
      {view === 'day' && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }} className="r-cal-week-scroll">
          <DayView />
        </div>
      )}
    </div>
  );
}

const st = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  stickyHeader: {
    position: 'sticky',
    top: 'var(--topbar-h, 61px)',
    zIndex: 20,
    background: '#f5f5f5',
    paddingBottom: 8,
    marginBottom: -8,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  navRow: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'center' },
  navBtn: { width: 36, height: 36, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  monthTitle: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', textTransform: 'capitalize', minWidth: 0, textAlign: 'center' },
  btnRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #edebe9' },
  viewBtn: { padding: '8px 16px', border: 'none', borderRight: '1px solid #edebe9', background: '#fff', fontSize: 13, fontWeight: 600, color: '#605e5c', cursor: 'pointer' },
  viewBtnActive: { background: '#E86E1C', color: '#fff' },
  todayBtn: { padding: '8px 16px', border: 'none', background: '#fff', color: '#E86E1C', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  legend: { display: 'flex', gap: 16, flexWrap: 'wrap', padding: '6px 0', alignItems: 'flex-start' },
  legGrp: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  legItem: { display: 'flex', alignItems: 'center', gap: 4 },
  legLbl: { fontSize: 11, color: '#605e5c' },
  weekContainer: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: '100%' },
  roosterTable: { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' },
  th: { padding: '6px 4px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', borderRight: '1px solid #edebe9', verticalAlign: 'middle' },
  naamCel: { padding: '4px 6px', fontSize: 12, fontWeight: 600, color: '#323130', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f2f1' },
  calendarCard: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  dayHeader: { padding: '10px 0', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#605e5c', textTransform: 'uppercase' },
  dayCell: { minHeight: 70, padding: 3, borderRight: '1px solid #f3f2f1', borderBottom: '1px solid #f3f2f1' },
  dayNumber: { fontSize: 11, fontWeight: 700, color: '#323130', marginBottom: 1 },
};

export default Calendar;
